import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { experienceActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { ExperienceApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { LoadPanel } from 'devextreme-react/load-panel';
import { formatCurrency, formatPercent } from '@apply/ui';
import { RepaymentsGrids } from '../../loans/common/LoanComponents';

const BfiDetailModal = () => {
    const { isOpen, id } = useSelector((state) => state.experiences?.bfiDetailModal);
    const { dispatch } = store;

    const experienceApi = new ExperienceApi.ExperienceApi(applicationInsights);

    const [loadingMessage, set_loadingMessage] = useState('');

    const [experience, set_experience] = useState(null);

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: { marginRight: '20px', textAlign: 'right' },
        principalSummary: {
            padding: '10px',
            backgroundColor: '#e6e6e6',
            marginBottom: '10px',
            borderRadius: '5px',
        },
        principalName: {
            fontSize: 'larger',
            color: '#2c5591',
            fontWeight: 'bold',
        },
        credits: {
            backgroundColor: 'linen',
            textAlign: 'center',
            cursor: 'auto',
        },
    };

    const loadExperience = async (id) => {
        if (!id) {
            return;
        }

        try {
            set_loadingMessage('Loading...');
            const response = await experienceApi.getExperienceBfi(id);
            if (!response || response.statusCode !== 200) {
                return;
            }

            set_experience(response.result);
        } finally {
            set_loadingMessage('');
        }
    };

    const onRenderCommitted = (exp) => {
        let committedAmt = null;
        if (exp.isPortfolio) {
            committedAmt = exp.commitment + exp.originationFee;
        } else {
            committedAmt = exp.promissoryNote;
        }

        if (!committedAmt) {
            return null;
        }
        return formatCurrency(committedAmt, 0);
    };

    useEffect(() => {
        resetState();

        if (!id) {
            return;
        }

        loadExperience(id);
    }, [id]);

    const resetState = () => {
        set_experience(null);
    };

    const _handleClose = () => {
        dispatch(
            experienceActions.setBfiDetailModal({
                bfiDetailModal: { isOpen: false },
            })
        );
    };

    const onRenderDate = (val, dfltVal) => {
        if (!val) return dfltVal;
        return new Date(val).toLocaleDateString('en-us');
    };

    if (!isOpen || !experience) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '950px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div>
                                    <div
                                        className='page-header'
                                        role='banner'
                                    >
                                        <h1>Experience Detail (BFI - {experience.isRepaid ? 'Repaid' : 'Outstanding'})</h1>
                                        <button
                                            type='button'
                                            className='close'
                                            data-dismiss='modal'
                                            aria-label='Close'
                                            onClick={_handleClose}
                                        >
                                            <span aria-hidden='true'>&times;</span>
                                        </button>
                                    </div>
                                    <form
                                        name='experienceForm'
                                        className='standard-form'
                                    >
                                        <div
                                            className='scrollable-content'
                                            style={{ margin: '10px' }}
                                        >
                                            <div style={styles.principalSummary}>
                                                <span style={styles.principalName}>{experience.principalName}</span>
                                            </div>

                                            <h2 className='decorated'>Loan</h2>
                                            <div
                                                style={{
                                                    padding: '0 10px 0 10px',
                                                }}
                                            >
                                                <div className='row'>
                                                    <div className='columns small-12 large-4'>
                                                        BFI #: <br />
                                                        <b>{experience.bfiNumber}</b>
                                                    </div>
                                                    <div className='columns small-12 large-8'>
                                                        Entity: <br />
                                                        <b>{experience.entityName}</b>
                                                    </div>
                                                    <div className='columns small-12 large-4'>
                                                        Note Date: <br />
                                                        <b>{onRenderDate(experience.noteDate, 'No Date Set')}</b>
                                                    </div>
                                                    <div className='columns small-12 large-3'>
                                                        Type: <br />
                                                        <b>{experience.loanType}</b>
                                                    </div>
                                                    <div className='columns small-12 large-3'>
                                                        Status: <br />
                                                        <b>{experience.loanStatus}</b>
                                                    </div>
                                                    <div className='columns small-12 large-3'>
                                                        Months: <br />
                                                        <b>{experience.monthsOutstanding}</b>
                                                    </div>
                                                </div>
                                            </div>

                                            <h2 className='decorated'>Property</h2>
                                            <div className='row'>
                                                <div className='columns small-12 large-3'>
                                                    Structure Type: <br />
                                                    <b>{experience.structureType}</b>
                                                </div>
                                                <div className='columns small-12 large-8'>
                                                    Units: <br />
                                                    <b>{experience.unitCount}</b>
                                                </div>
                                                <div className='columns small-12 large-1'>
                                                    <label htmlFor='credits'>Credits</label>
                                                    <input
                                                        type='text'
                                                        id='credits'
                                                        value={experience.credits ?? ''}
                                                        required={false}
                                                        disabled={true}
                                                        style={styles.credits}
                                                        title='Credits are only calculated for Verified experience'
                                                    />
                                                </div>
                                            </div>
                                            <div className='row'>
                                                <div className='columns small-12 large-3'>
                                                    Committed Amount: <br />
                                                    <b>{onRenderCommitted(experience)}</b>
                                                </div>

                                                <div className='columns small-12 large-3'>
                                                    Repaid Amount: <br />
                                                    <b>{formatCurrency(experience.repaidAmount, 0)}</b>
                                                </div>
                                                <div className='columns small-12 large-3'>
                                                    {experience.isRepaid ? 'Repaid Date' : 'Last Pay Date'}
                                                    : <br />
                                                    <b>{onRenderDate(experience.lastPayDate, 'No Payments Made')}</b>
                                                </div>
                                                {!experience.isRepaid && (
                                                    <div className='columns small-12 large-3'>
                                                        Outstanding: <br />
                                                        <b>{formatCurrency(experience.outstandingAmount, 0)}</b>
                                                    </div>
                                                )}

                                                <div className='columns small-12 large-3'>
                                                    Appraised Value: <br />
                                                    <b>{formatCurrency(experience.appraisedValue, 0)}</b>
                                                </div>
                                                <div className='columns small-12 large-3'>
                                                    Appraised Date: <br />
                                                    <b>{onRenderDate(experience.AppraisedDate, 'No Appraisal done')}</b>
                                                </div>
                                                <div className='columns small-12 large-3'>
                                                    LTV: <br />
                                                    <b>{formatPercent(experience.ltv, 1)}</b>
                                                </div>
                                                {experience.salePrice && (
                                                    <div className='columns small-12 large-3'>
                                                        Sale Price: <br />
                                                        <b>{formatCurrency(experience.salePrice, 0)}</b>
                                                    </div>
                                                )}
                                            </div>
                                            <div className='row'>
                                                <div className='columns small-12 large-12'>
                                                    Address: <br />
                                                    <b>
                                                        {experience.street1}
                                                        {experience.street2 && <span>, {experience.street2}</span>}
                                                        <br />
                                                        {experience.city}
                                                        <br />
                                                        {experience.state}, {experience.zipCode}
                                                        <br />
                                                        {experience.county}
                                                    </b>
                                                </div>
                                            </div>
                                            <RepaymentsGrids
                                                propertyId={experience.propertyId}
                                                editable={false}
                                            />
                                        </div>
                                        <div
                                            className='button-footer'
                                            style={styles.buttonContainer}
                                        >
                                            <button
                                                type='button'
                                                className='button hollow'
                                                data-dismiss='modal'
                                                onClick={_handleClose}
                                            >
                                                Close
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </>
    );
};

export default BfiDetailModal;
